import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { ArrowClockwise, ChartLineUp, PencilSimple, Check, X, TrendUp, Target, CheckCircle, Warning } from "@phosphor-icons/react";

const fmtINR = (paise) => {
  const n = Number(paise || 0) / 100;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
};
const monthLabel = (isoMonth) => {
  const [y, m] = isoMonth.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

// Default window: this month → next Indian FY end (March). If today is Aug
// 2026 this yields Aug 2026 → Mar 2027 (8 months); if today is Feb 2026 this
// yields Feb 2026 → Mar 2026 (2 months). Only forward-looking data — no
// historical months from HubSpot.
const defaultWindow = () => {
  const now = new Date();
  const from = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  const toYear = now.getUTCMonth() <= 2 ? now.getUTCFullYear() : now.getUTCFullYear() + 1; // getUTCMonth: 0-11
  const to = `${toYear}-03`;
  return { from, to };
};

const TargetInline = ({ month, targetPaise, field = "target_paise", canEdit, onSaved, testidPrefix = "target" }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(Math.round((targetPaise || 0) / 100))); // rupees
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(String(Math.round((targetPaise || 0) / 100))); }, [targetPaise]);

  const save = async () => {
    const rupees = Number(val.replace(/,/g, ""));
    if (isNaN(rupees) || rupees < 0) return toast.error("Enter a valid amount in ₹");
    setSaving(true);
    try {
      await api.put(`/revenue/targets/${month}`, { [field]: Math.round(rupees * 100) });
      toast.success(`${monthLabel(month)} ${field === "target_paise" ? "MRR" : "one-time"} target saved`);
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="font-bold tabular-nums" data-testid={`${testidPrefix}-${month}`}>{fmtINR(targetPaise)}</span>
        {canEdit && (
          <button onClick={() => setEditing(true)} data-testid={`${testidPrefix}-edit-${month}`} className="text-slate-400 hover:text-blue-600" aria-label={`Edit ${month} ${field}`}>
            <PencilSimple size={12}/>
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-slate-500">₹</span>
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
             data-testid={`${testidPrefix}-input-${month}`} className="w-24 border border-slate-300 rounded-sm px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 tabular-nums"/>
      <button onClick={save} disabled={saving} data-testid={`${testidPrefix}-save-${month}`} className="text-emerald-700 hover:bg-emerald-50 rounded-sm p-1"><Check size={12} weight="bold"/></button>
      <button onClick={() => setEditing(false)} data-testid={`${testidPrefix}-cancel-${month}`} className="text-red-600 hover:bg-red-50 rounded-sm p-1"><X size={12} weight="bold"/></button>
    </div>
  );
};

const AttainmentBar = ({ pct }) => {
  const clamped = Math.min(Math.max(pct || 0, 0), 150);
  const barColor = clamped >= 100 ? "bg-emerald-500" : clamped >= 70 ? "bg-blue-500" : clamped >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(clamped, 100)}%` }}/>
    </div>
  );
};

export default function RevenueTrackerPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("admin");
  const [win, setWin] = useState(defaultWindow());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const { data: resp } = await api.get(`/revenue/summary?from_month=${win.from}&to_month=${win.to}`);
      setData(resp);
    } catch (e) { setError(e?.response?.data?.detail || "Failed to load revenue summary"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [win.from, win.to]);

  const totals = useMemo(() => {
    if (!data?.months) return { mrr: 0, one_time: 0, target: 0, won: 0, pipeline_open: 0 };
    return data.months.reduce((acc, m) => ({
      mrr: acc.mrr + (m.target_paise || 0),
      one_time: acc.one_time + (m.one_time_target_paise || 0),
      target: acc.target + (m.combined_target_paise || 0),
      won: acc.won + (m.closed_won_paise || 0),
      pipeline_open: acc.pipeline_open + (m.pipeline_open_paise || 0),
    }), { mrr: 0, one_time: 0, target: 0, won: 0, pipeline_open: 0 });
  }, [data]);
  const overallPct = totals.target > 0 ? Math.round((totals.won / totals.target) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="revenue-tracker-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">The scorecard</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1 flex items-center gap-2"><ChartLineUp size={28} weight="fill" className="text-blue-600"/>Revenue tracker</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            Monthly closed-won revenue auto-computed from your CRM · targets editable by admins {data?.pipeline_label && <>· <b>{data.pipeline_label}</b></>}
            {data?.data_source === "internal_crm" && (
              <span data-testid="rev-data-source-badge" className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                Internal CRM
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-600 flex items-center gap-2">
            From
            <input type="month" value={win.from} onChange={(e) => setWin({ ...win, from: e.target.value })} data-testid="rev-from" className="border border-slate-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </label>
          <label className="text-xs text-slate-600 flex items-center gap-2">
            To
            <input type="month" value={win.to} onChange={(e) => setWin({ ...win, to: e.target.value })} data-testid="rev-to" className="border border-slate-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </label>
          <button onClick={load} disabled={busy} data-testid="rev-refresh" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1 hover:bg-slate-100 disabled:opacity-50">
            <ArrowClockwise size={11} className={busy ? "animate-spin" : ""}/>{busy ? "Loading" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 border border-red-200 bg-red-50 text-red-800 text-sm p-3 rounded-sm">{error}</div>
      )}
      {data?.revenue_error && (
        <div className="mt-4 border border-amber-200 bg-amber-50 text-amber-900 text-xs p-3 rounded-sm flex items-center gap-2" data-testid="rev-warn">
          <Warning size={14}/> {data.revenue_error}
        </div>
      )}

      {/* Totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6" data-testid="rev-kpis">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-1.5 label-micro text-slate-600"><Target size={12} className="text-blue-600"/>MRR Target</div>
          <div className="font-display text-2xl font-black text-slate-900 tabular-nums mt-2" data-testid="rev-total-mrr">{fmtINR(totals.mrr)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Recurring subscription target</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-1.5 label-micro text-slate-600"><Target size={12} className="text-indigo-600"/>One-time Fee Target</div>
          <div className="font-display text-2xl font-black text-slate-900 tabular-nums mt-2" data-testid="rev-total-onetime">{fmtINR(totals.one_time)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Setup / hardware / implementation</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-1.5 label-micro text-slate-600"><CheckCircle size={12} className="text-emerald-600"/>Closed-won</div>
          <div className="font-display text-2xl font-black text-slate-900 tabular-nums mt-2" data-testid="rev-total-won">{fmtINR(totals.won)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Auto from your CRM</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-1.5 label-micro text-slate-600"><TrendUp size={12} className="text-blue-600"/>Open pipeline</div>
          <div className="font-display text-2xl font-black text-slate-900 tabular-nums mt-2">{fmtINR(totals.pipeline_open)}</div>
          <div className="text-[11px] text-slate-500 mt-1">By expected close date</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-1.5 label-micro text-slate-600">Attainment</div>
          <div className="font-display text-2xl font-black tabular-nums mt-2" data-testid="rev-attainment">
            <span className={overallPct >= 100 ? "text-emerald-700" : overallPct >= 70 ? "text-blue-700" : overallPct >= 40 ? "text-amber-700" : "text-red-700"}>{overallPct}%</span>
          </div>
          <div className="mt-2"><AttainmentBar pct={overallPct}/></div>
          <div className="text-[11px] text-slate-500 mt-1">vs MRR + one-time combined</div>
        </div>
      </div>

      {/* Monthly table */}
      <div className="mt-8 bg-white border border-slate-200 rounded-sm" data-testid="rev-monthly">
        <div className="px-4 sm:px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-slate-900">Month-by-month</h3>
            <p className="text-xs text-slate-500 mt-0.5">{isAdmin ? "Click any target to edit it inline" : "Targets are set by admins"}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <tr className="text-left">
                <th className="px-4 sm:px-5 py-2.5 label-micro">Month</th>
                <th className="px-4 sm:px-5 py-2.5 label-micro">MRR Target</th>
                <th className="px-4 sm:px-5 py-2.5 label-micro">One-time Fee Target</th>
                <th className="px-4 sm:px-5 py-2.5 label-micro">Closed-won</th>
                <th className="px-4 sm:px-5 py-2.5 label-micro">Open pipeline</th>
                <th className="px-4 sm:px-5 py-2.5 label-micro">Deficit</th>
                <th className="px-4 sm:px-5 py-2.5 label-micro">Attainment</th>
              </tr>
            </thead>
            <tbody>
              {(data?.months || []).map((m) => (
                <tr key={m.month} className="border-b border-slate-100 hover:bg-blue-50/40" data-testid={`rev-row-${m.month}`}>
                  <td className="px-4 sm:px-5 py-3 font-semibold text-slate-900">{monthLabel(m.month)}</td>
                  <td className="px-4 sm:px-5 py-3">
                    <TargetInline month={m.month} targetPaise={m.target_paise} field="target_paise" canEdit={isAdmin} onSaved={load} testidPrefix="mrr-target"/>
                  </td>
                  <td className="px-4 sm:px-5 py-3">
                    <TargetInline month={m.month} targetPaise={m.one_time_target_paise} field="one_time_target_paise" canEdit={isAdmin} onSaved={load} testidPrefix="onetime-target"/>
                  </td>
                  <td className="px-4 sm:px-5 py-3 tabular-nums">
                    <div className="font-semibold text-emerald-700">{fmtINR(m.closed_won_paise)}</div>
                    <div className="text-[11px] text-slate-500">{m.closed_won_count} deal{m.closed_won_count === 1 ? "" : "s"}</div>
                  </td>
                  <td className="px-4 sm:px-5 py-3 tabular-nums">
                    <div className="text-slate-700">{fmtINR(m.pipeline_open_paise)}</div>
                    <div className="text-[11px] text-slate-500">{m.pipeline_open_count} open</div>
                  </td>
                  <td className="px-4 sm:px-5 py-3 tabular-nums">
                    <span className={m.deficit_paise > 0 ? "text-red-600 font-semibold" : "text-emerald-700"}>{m.deficit_paise > 0 ? fmtINR(m.deficit_paise) : "—"}</span>
                  </td>
                  <td className="px-4 sm:px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24"><AttainmentBar pct={m.attainment_pct}/></div>
                      <span className="text-xs font-bold tabular-nums w-12">{m.attainment_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {(!data || data.months?.length === 0) && (
                <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-400 text-sm">No months in range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
